import { createFileRoute, Link } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useState } from 'react';
import ActionButton from '@/components/Button/ActionButton';
import { Card } from '@/components/Card/card';
import { HStack, Spacer, VStack } from '@/components/Layout';
import { Text } from '@/components/Typography/Text';
import { useToast } from '@/components/ui/use-toast';
import DownloadTemplateCard from '@/features/import-csv/-components/-upload-file/DownloadTemplateCard';
import { ImportStepperHeader, isImportConflictError, UploadFileCard, type UploadedCsvFile, useEnqueueImportJob, usePeopleCsvTemplateInfo } from '@/features/people-csv-import';
import { useCommonTranslation } from '@/hooks/useCommonTranslation';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { CsvImportUploadSearchSchema } from './-zod-schema';
const CsvImportUpload = () => {
  const {
    type
  } = Route.useSearch();
  const navigate = Route.useNavigate();
  const {
    toast
  } = useToast();
  const tc = useCommonTranslation();
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.person.csvImport'
  });
  const [uploadedFile, setUploadedFile] = useState<UploadedCsvFile | undefined>(undefined);
  const {
    mutate: enqueue,
    isPending
  } = useEnqueueImportJob(type);
  const {
    exportApiUrl,
    exportFilename,
    templateTitle,
    updateExisting
  } = usePeopleCsvTemplateInfo(type);
  const handleUpload = () => {
    if (!uploadedFile) {
      return;
    }
    enqueue({
      uploadedFileKey: uploadedFile.uploadedFileKey,
      originalFileName: uploadedFile.originalFileName,
      encoding: 'UTF-8'
    }, {
      onSuccess: ({
        importId
      }) => {
        navigate({
          to: '/person/csv-import/$importId',
          params: {
            importId
          }
        });
      },
      onError: error => {
        toast({
          description: isImportConflictError(error) ? t('errors.conflict') : t('errors.enqueueFailed'),
          variant: 'destructive'
        });
      }
    });
  };
  return <VStack className="w-full items-start gap-6 p-10">
      <HStack className="items-center gap-1">
        <Link to="/person/csv-import" search={{
        type
      }}>
          <Text variant="labelReg" className="text-neutral-muted-on-fill">
            {t('breadcrumb')}
          </Text>
        </Link>
      </HStack>

      <ImportStepperHeader currentStep={1} />

      <DownloadTemplateCard exportApiUrl={exportApiUrl} exportFilename={exportFilename} downloadOptions={[{
      mode: 'edit-template',
      label: templateTitle
    }, {
      mode: 'edit',
      label: updateExisting
    }]} />
      <UploadFileCard onUploaded={setUploadedFile} />

      <Spacer size={8} />

      <Card className="w-full">
        <HStack className="w-full items-center justify-between px-6 py-4">
          <Link to="/person/csv-import" search={{
          type
        }}>
            <ActionButton variant="ghost">{tc('action.cancel')}</ActionButton>
          </Link>
          <ActionButton variant="default" icon="ArrowRightIcon" iconPosition="right" isLoading={isPending} disabled={!uploadedFile} onClick={handleUpload}>
            {tc('action.next')}
          </ActionButton>
        </HStack>
      </Card>
    </VStack>;
};
export const Route = createFileRoute('/_staff/person/csv-import/upload')({
  validateSearch: zodValidator(CsvImportUploadSearchSchema),
  component: CsvImportUpload
});